import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  Gift, Copy, ShareNetwork, Trophy, Users, Coins, CheckCircle,
  Sparkle, Medal, Clock, SignIn, Wrench, Handshake, Compass,
  CaretDown, Lock, BookOpen,
} from "@phosphor-icons/react";
import api, { formatBRL, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useAuthModal } from "../lib/authModal";
import useFocusRefresh from "../lib/useFocusRefresh";
import usePullToRefresh from "../lib/usePullToRefresh";
import PullIndicator from "../components/PullIndicator";
import { AchievementIcon } from "../lib/achievementIcons";
import ShareAchievement from "../components/ShareAchievement";
import { shareContent } from "../lib/native";

function ProgressBar({ value, max, color = "#16A34A", testId }) {
  const pct = Math.max(0, Math.min(100, max > 0 ? (value / max) * 100 : 100));
  return (
    <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden" data-testid={testId}>
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
}

// V14.6: categoria de conquistas — mostra a medalha mais recente; expande p/ ver a evolução
function CategoryMedals({ group, meta }) {
  const [open, setOpen] = useState(false);
  const latest = group.latest;
  const hasMore = group.all.length > 1;
  return (
    <div className="rounded-2xl bg-white/[0.07] border border-white/10 p-3" data-testid={`achievement-category-${group.key}`}>
      <div className="flex items-center gap-3">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center relative shrink-0 shadow-sm"
          style={{ background: `radial-gradient(circle at 35% 30%, ${latest.color}55, ${latest.color}22)`, border: `2px solid ${latest.color}` }}
        >
          <AchievementIcon icon={latest.icon} size={26} style={{ color: "#fff" }} />
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#16A34A] flex items-center justify-center ring-2 ring-[#1E293B]">
            <CheckCircle size={11} weight="fill" className="text-white" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wide text-white/55 font-bold truncate">{group.title}</div>
          <div className="text-sm font-black text-white leading-tight truncate">{latest.label}</div>
          {hasMore && (
            <button onClick={() => setOpen((o) => !o)} className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-[#FFD166]" data-testid={`achievement-expand-${group.key}`}>
              Ver evolução ({group.all.length})
              <CaretDown size={11} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
        <ShareAchievement
          achievement={latest} code={meta.code} link={meta.link} userName={meta.userName} userAvatar={meta.userAvatar} compact
          className="shrink-0 w-9 h-9 rounded-full bg-[#FF6B35] text-white flex items-center justify-center"
        />
      </div>

      {open && hasMore && (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-2" data-testid={`achievement-evolution-${group.key}`}>
          {group.all.map((a, i) => (
            <div key={i} className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: `${a.color}33`, border: `1.5px solid ${a.color}` }}>
                <AchievementIcon icon={a.icon} size={15} style={{ color: "#fff" }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-white/90 truncate">{a.label}</div>
                <div className="text-[10px] text-white/45">{a.count}</div>
              </div>
              {a === latest && <span className="text-[10px] font-bold text-[#FFD166]">Atual</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReferralPanel({ ch, onCopy, onShare, copied }) {
  const s = ch.stats || {};
  return (
    <div className="space-y-3 pt-1" data-testid="referral-panel">
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-600 truncate" data-testid="rewards-link-value">
          {ch.link}
        </div>
        <button onClick={onCopy} className="shrink-0 w-11 h-11 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-700" aria-label="Copiar link" data-testid="rewards-copy-btn">
          {copied ? <CheckCircle size={20} weight="fill" className="text-[#16A34A]" /> : <Copy size={20} />}
        </button>
      </div>
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <span>Seu código:</span>
        <span className="font-mono font-black text-[#004E98] tracking-wider" data-testid="rewards-code">{ch.code}</span>
      </div>
      <button onClick={onShare} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#FF6B35] text-white font-bold" data-testid="rewards-share-btn">
        <ShareNetwork size={20} weight="bold" /> Compartilhar convite
      </button>

      <div className="grid grid-cols-3 gap-2" data-testid="rewards-stats">
        <div className="rounded-xl bg-slate-50 p-3 text-center" data-testid="stat-total-earned">
          <div className="text-base font-black text-[#16A34A] leading-none">{formatBRL(s.total_earned)}</div>
          <div className="text-[10px] text-slate-500 mt-1">Total ganho</div>
        </div>
        <div className="rounded-xl bg-slate-50 p-3 text-center" data-testid="stat-invited">
          <div className="text-base font-black text-slate-800 leading-none">{s.invited || 0}</div>
          <div className="text-[10px] text-slate-500 mt-1">Convidados</div>
        </div>
        <div className="rounded-xl bg-slate-50 p-3 text-center" data-testid="stat-rewarded">
          <div className="text-base font-black text-slate-800 leading-none">{s.rewarded || 0}</div>
          <div className="text-[10px] text-slate-500 mt-1">Recompensados</div>
        </div>
      </div>

      {ch.invited_status && ch.invited_status.status !== "rewarded" && (
        <div className="rounded-xl p-3 border-2 border-[#004E98]/15 bg-[#004E98]/[0.03] space-y-2" data-testid="rewards-invited-card">
          <div className="text-xs font-bold text-[#004E98] flex items-center gap-1"><Sparkle size={14} weight="fill" /> Você foi convidado!</div>
          <p className="text-[11px] text-slate-600">Complete {formatBRL(ch.invited_status.threshold)} em locações para liberar a recompensa do seu amigo. Já são {formatBRL(ch.invited_status.progress)}.</p>
          <ProgressBar value={ch.invited_status.progress} max={ch.invited_status.threshold} color="#004E98" testId="rewards-invited-bar" />
        </div>
      )}

      {(ch.referrals || []).length > 0 && (
        <div className="space-y-2" data-testid="rewards-list">
          <div className="text-xs font-bold text-slate-600">Seus convidados</div>
          {ch.referrals.map((r, i) => (
            <div key={i} className="rounded-xl bg-slate-50 p-3 space-y-2" data-testid={`referral-row-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-sm text-slate-800 truncate">{r.invited_name}</span>
                {r.status === "rewarded" ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold" data-testid={`referral-status-${i}`}>
                    <CheckCircle size={12} weight="fill" /> +{formatBRL(r.reward_amount)}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-bold" data-testid={`referral-status-${i}`}>
                    <Clock size={12} weight="fill" /> Pendente
                  </span>
                )}
              </div>
              {r.status !== "rewarded" && (
                <>
                  <ProgressBar value={r.progress} max={r.threshold} color="#FF6B35" testId={`referral-bar-${i}`} />
                  <div className="text-[10px] text-slate-400">{formatBRL(r.progress)} de {formatBRL(r.threshold)}</div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MinigameCard({ ch, onCopy, onShare, copied }) {
  const [open, setOpen] = useState(ch.key === "referral");
  const [showRules, setShowRules] = useState(false);
  const next = ch.next_milestone;
  const level = ch.current_level || "Iniciante";
  const barValue = next ? ch.current - (next.from || 0) : 1;
  const barMax = next ? next.count - (next.from || 0) : 1;

  return (
    <div className="aj-card overflow-hidden" data-testid={`minigame-${ch.key}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left p-4 space-y-3"
        data-testid={`minigame-toggle-${ch.key}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: ch.color + "1A" }}>
            <AchievementIcon icon={ch.icon} size={24} style={{ color: ch.color }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-black text-sm text-slate-900 leading-tight">{ch.title}</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: ch.color + "1A", color: ch.color }}>
                <Trophy size={11} weight="fill" /> {level}
              </span>
              <span className="text-[10px] text-slate-400">Nível {ch.level_index}/{ch.level_total}</span>
            </div>
          </div>
          <CaretDown size={18} className={`text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
        <div className="space-y-1">
          <ProgressBar value={barValue} max={barMax} color={ch.color} testId={`minigame-bar-${ch.key}`} />
          <div className="flex items-center justify-between text-[11px] text-slate-500">
            <span>{ch.current} {ch.unit}</span>
            {next ? (
              <span data-testid={`minigame-next-${ch.key}`}>Falta {next.remaining} p/ <b style={{ color: ch.color }}>{next.label}</b></span>
            ) : (
              <span className="font-bold text-[#16A34A]">Nível máximo 🏆</span>
            )}
          </div>
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3" data-testid={`minigame-content-${ch.key}`}>
          {/* Regras colapsáveis */}
          <div>
            <button onClick={() => setShowRules((r) => !r)} className="flex items-center gap-1.5 text-xs font-bold text-slate-600" data-testid={`minigame-rules-toggle-${ch.key}`}>
              <BookOpen size={14} weight="bold" /> Como funciona
              <CaretDown size={12} className={`transition-transform ${showRules ? "rotate-180" : ""}`} />
            </button>
            {showRules && (
              <p className="text-xs text-slate-500 mt-2 leading-relaxed" data-testid={`minigame-rules-${ch.key}`}>{ch.rules}</p>
            )}
          </div>

          {/* Marcos / medalhas */}
          <div className="space-y-1.5">
            {ch.milestones.map((m, i) => {
              return (
                <div key={i} className={`flex items-center gap-2.5 rounded-xl px-3 py-2 ${m.achieved ? "bg-emerald-50/60" : "bg-slate-50"}`} data-testid={`milestone-${ch.key}-${i}`}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: m.achieved ? m.color + "22" : "#E2E8F0" }}>
                    {m.achieved ? <AchievementIcon icon={m.icon || ch.icon} size={16} style={{ color: m.color }} /> : <Lock size={14} weight="bold" className="text-slate-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-bold ${m.achieved ? "text-slate-800" : "text-slate-500"}`}>{m.label}</div>
                    <div className="text-[10px] text-slate-400">{m.count} {ch.unit}</div>
                  </div>
                  {m.achieved ? (
                    <CheckCircle size={18} weight="fill" className="text-[#16A34A] shrink-0" />
                  ) : ch.money_reward && m.reward > 0 ? (
                    <span className="text-[11px] font-bold text-[#16A34A] shrink-0">{formatBRL(m.reward)}</span>
                  ) : null}
                </div>
              );
            })}
          </div>

          {ch.key === "referral" && <ReferralPanel ch={ch} onCopy={onCopy} onShare={onShare} copied={copied} />}
        </div>
      )}
    </div>
  );
}

export default function Recompensas() {
  const { user } = useAuth();
  const { setOpen } = useAuthModal();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const { data } = await api.get("/gamification/me");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useFocusRefresh(load);
  const ptr = usePullToRefresh(load);

  const copyLink = async () => {
    if (!data?.link) return;
    try {
      await navigator.clipboard.writeText(data.link);
      setCopied(true);
      toast.success("Link copiado! Mande para seus amigos.");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.info("Copie manualmente: " + data.link); }
  };
  const shareLink = async () => {
    if (!data?.link) return;
    // Link também no texto: garante a mensagem completa mesmo em apps que ignoram o campo url.
    const text = `Alugue ferramentas e equipamentos no AlugaJá! Use meu convite e a gente ganha junto. 🛠️ ${data.link}`;
    // Compartilhamento NATIVO (Capacitor Share) no app; navigator.share no navegador;
    // fallback final = copiar o link. Garante a folha de compartilhamento no Android/iOS.
    const res = await shareContent({ title: "AlugaJá — Indique e Ganhe", text, url: data.link, dialogTitle: "Compartilhar convite" });
    if (res.method === "clipboard" && res.ok) {
      setCopied(true);
      toast.success("Link copiado! Mande para seus amigos.");
      setTimeout(() => setCopied(false), 2000);
    } else if (!res.ok && res.method === "none") {
      await copyLink();
    }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="rewards-login-required">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-[#FF6B35]/10 flex items-center justify-center mb-4">
          <Trophy size={32} weight="fill" className="text-[#FF6B35]" />
        </div>
        <h1 className="text-2xl font-black text-slate-900 mb-2">Recompensas</h1>
        <p className="text-sm text-slate-500 mb-6">Entre na sua conta para ganhar medalhas, completar desafios e receber recompensas de verdade.</p>
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#004E98] text-white font-bold" data-testid="rewards-login-btn">
          <SignIn size={20} weight="bold" /> Entrar ou criar conta
        </button>
      </div>
    );
  }

  if (loading || !data) {
    return <div className="max-w-md mx-auto px-4 py-16 text-center text-slate-400" data-testid="rewards-loading">Carregando suas recompensas...</div>;
  }

  const groupedAchievements = (() => {
    const map = {};
    for (const a of data.achievements || []) (map[a.challenge_key] ||= []).push(a);
    return Object.entries(map).map(([key, list]) => {
      const all = [...list].sort((x, y) => y.count - x.count);
      return { key, title: all[0].challenge_title, latest: all[0], all };
    });
  })();
  const meta = { code: data.code, link: data.link, userName: user?.name, userAvatar: user?.avatar_url };

  return (
    <div className="max-w-2xl mx-auto px-4 py-5 space-y-5 pb-10" data-testid="rewards-page">
      <PullIndicator pulling={ptr.isPulling} progress={ptr.progress} refreshing={ptr.isRefreshing} />
      {/* Header */}
      <div className="flex items-center gap-3" data-testid="rewards-header">
        <div className="w-12 h-12 rounded-2xl bg-[#FF6B35] flex items-center justify-center text-white shrink-0">
          <Trophy size={26} weight="fill" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-slate-900 leading-none">Recompensas</h1>
          <p className="text-xs text-slate-500 mt-1">Complete desafios e ganhe medalhas e prêmios</p>
        </div>
      </div>

      {/* Conquistas (medalhas já atingidas) */}
      <div className="rounded-3xl p-5 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1E293B 0%, #004E98 130%)" }} data-testid="achievements-section">
        <div className="absolute -right-4 -top-4 opacity-15"><Medal size={110} weight="fill" /></div>
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <Medal size={18} weight="fill" className="text-[#FFD166]" />
            <span className="text-sm font-black">Minhas conquistas</span>
            <span className="ml-auto text-xs bg-white/15 px-2 py-0.5 rounded-full font-bold" data-testid="achievements-count">{data.achievements_count} medalha{data.achievements_count === 1 ? "" : "s"}</span>
          </div>
          {data.achievements.length === 0 ? (
            <p className="text-xs text-white/75" data-testid="achievements-empty">Você ainda não tem medalhas. Complete os desafios abaixo para começar a colecionar! 🏅</p>
          ) : (
            <div className="space-y-2" data-testid="achievements-list">
              {groupedAchievements.map((g) => <CategoryMedals key={g.key} group={g} meta={meta} />)}
            </div>
          )}
        </div>
      </div>

      {/* Minigames */}
      <div className="space-y-3" data-testid="minigames-list">
        <div className="text-sm font-bold text-slate-700 px-1">Desafios</div>
        {data.challenges.map((ch) => (
          <MinigameCard key={ch.key} ch={ch} onCopy={copyLink} onShare={shareLink} copied={copied} />
        ))}
      </div>
    </div>
  );
}
